// Leaders Bonus card colouring (Alex, 2026-09-28). Four zones, the Race
// card's vocabulary and palette (DashboardRace: Flying 🔥 / On track 😎 /
// Behind 😮 / Danger zone ⚠️), scored against the PAY PERIOD's own
// targets. Icons follow the Team dashboard's on-time bands (trophy /
// tick / warning) so the two pages read alike.
//
// Items (booked in, put away) are judged on PACE: units so far ÷ units
// expected by now, where "expected" = daily target × working days
// completed + today's share by the clock (06:00–17:00). On-time is judged
// on the period rate vs the target %.

export type Zone = "flying" | "on_track" | "behind" | "danger";
export type BonusBand = { zone: Zone; colour: string; icon: "warning" | "tick" | "trophy" };

export const BAND_FLYING = "#22c55e";
export const BAND_GREEN = "#0a8f3c";
export const BAND_AMBER = "#f59e0b";
export const BAND_RED = "#ef4444";

export const ZONE_LABEL_KEY: Record<Zone, string> = {
    flying: "dashboard_team_race_status_flying",
    on_track: "dashboard_team_race_status_on_track",
    behind: "dashboard_team_race_status_behind",
    danger: "dashboard_team_race_status_danger",
};
export const ZONE_EMOJI: Record<Zone, string> = { flying: "🔥", on_track: "😎", behind: "😮", danger: "⚠️" };

export const zoneBand = (zone: Zone): BonusBand => {
    switch (zone) {
        case "flying": return { zone, colour: BAND_FLYING, icon: "trophy" };
        case "on_track": return { zone, colour: BAND_GREEN, icon: "tick" };
        case "behind": return { zone, colour: BAND_AMBER, icon: "warning" };
        default: return { zone: "danger", colour: BAND_RED, icon: "warning" };
    }
};

// On-time: Flying from halfway between the target and 100 (target 98 →
// 99+); Behind down to this many points under the target; Danger below.
const ON_TIME_BEHIND_BELOW = 3;

/** Period on-time rate vs the target %. */
export const onTimeBandFor = (pct: number, targetPct: number): BonusBand => {
    const flyingFrom = Math.min(100, targetPct + (100 - targetPct) / 2);
    if (pct >= flyingFrom) return zoneBand("flying");
    if (pct >= targetPct) return zoneBand("on_track");
    if (pct >= targetPct - ON_TIME_BEHIND_BELOW) return zoneBand("behind");
    return zoneBand("danger");
};

/** Days below target: none = Flying, one = Behind, two or more = Danger. */
export const failDaysBand = (days: number): BonusBand => {
    if (days <= 0) return zoneBand("flying");
    if (days === 1) return zoneBand("behind");
    return zoneBand("danger");
};

// Pace ratio thresholds: ≥ 110% of expected = Flying; ≥ 97% = On track
// (a little slack for the clock proration); ≥ 85% = Behind; below = Danger.
const PACE_FLYING_FROM = 1.10;
const PACE_ON_TRACK_FROM = 0.97;
const PACE_BEHIND_FROM = 0.85;

/** Period-to-date units vs the units expected by now. The period target
 *  already reached is Flying; before anything is expected it reads On track. */
export const paceBand = (units: number, expectedToDate: number, periodTarget: number): BonusBand => {
    if (periodTarget > 0 && units >= periodTarget) return zoneBand("flying");
    if (expectedToDate <= 0) return zoneBand("on_track");
    const ratio = units / expectedToDate;
    if (ratio >= PACE_FLYING_FROM) return zoneBand("flying");
    if (ratio >= PACE_ON_TRACK_FROM) return zoneBand("on_track");
    if (ratio >= PACE_BEHIND_FROM) return zoneBand("behind");
    return zoneBand("danger");
};

/** A day bar's colour vs the daily target: met = green, ≥ 80% = amber, else red. */
export const dailyBarColour = (units: number, dailyTarget: number): string => {
    if (dailyTarget <= 0) return BAND_GREEN;
    if (units >= dailyTarget) return BAND_GREEN;
    if (units >= dailyTarget * 0.8) return BAND_AMBER;
    return BAND_RED;
};

/** Whole-percent progress toward the period target, capped at 100 for bars. */
export const progressPct = (units: number, target: number): number =>
    target > 0 ? Math.min(100, Math.round((units / target) * 100)) : 0;
